import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CMS_POST_API_CONFIG } from '../config';
import { ApiResponse, PaginatedResponse } from '../models';
import {
  VideoSeries,
  VideoSeriesRoute,
  CreateVideoSeriesDto,
  UpdateVideoSeriesDto,
  AddVideoEpisodeDto,
  ReorderVideoEpisodesDto,
  VideoSeriesListParams,
} from '../models/video-series.model';

@Injectable({ providedIn: 'root' })
export class VideoSeriesService {
  private readonly _http = inject(HttpClient);
  private readonly _apiBase: string;

  private readonly _readerBase: string;

  constructor() {
    const config = inject(CMS_POST_API_CONFIG);
    this._apiBase = `${config.apiBaseUrl}/api/video-series`;
    this._readerBase = `${config.apiBaseUrl}/api/reader/video-series`;
  }

  // ─── Public (unauthenticated) ──────────────────────────────────────────────

  getPublicVideoSeries(
    params: Omit<VideoSeriesListParams, 'publishStatus'>
  ): Observable<PaginatedResponse<VideoSeries>> {
    const httpParams = new HttpParams({ fromObject: this._toStringParams(params as Record<string, unknown>) });
    return this._http.get<PaginatedResponse<VideoSeries>>(`${this._apiBase}/public`, { params: httpParams });
  }

  getPublicVideoSeriesBySlug(slug: string, orgId: string): Observable<ApiResponse<VideoSeries>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<VideoSeries>>(`${this._apiBase}/public/${slug}`, { params: httpParams });
  }

  getPublicVideoSeriesRoutes(orgId: string): Observable<ApiResponse<VideoSeriesRoute[]>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<VideoSeriesRoute[]>>(`${this._apiBase}/public/routes`, { params: httpParams });
  }

  // ─── Authenticated ─────────────────────────────────────────────────────────

  listVideoSeries(params: VideoSeriesListParams): Observable<PaginatedResponse<VideoSeries>> {
    const httpParams = new HttpParams({ fromObject: this._toStringParams(params as Record<string, unknown>) });
    return this._http.get<PaginatedResponse<VideoSeries>>(this._apiBase, { params: httpParams });
  }

  getVideoSeriesById(id: string, orgId: string): Observable<ApiResponse<VideoSeries>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<VideoSeries>>(`${this._apiBase}/${id}`, { params: httpParams });
  }

  createVideoSeries(dto: CreateVideoSeriesDto): Observable<ApiResponse<VideoSeries>> {
    return this._http.post<ApiResponse<VideoSeries>>(this._apiBase, dto);
  }

  updateVideoSeries(id: string, dto: UpdateVideoSeriesDto): Observable<ApiResponse<VideoSeries>> {
    return this._http.patch<ApiResponse<VideoSeries>>(`${this._apiBase}/${id}`, dto);
  }

  publishVideoSeries(
    id: string,
    publishStatus: 'draft' | 'internal' | 'public' = 'public'
  ): Observable<ApiResponse<VideoSeries>> {
    return this._http.patch<ApiResponse<VideoSeries>>(`${this._apiBase}/${id}/publish`, { publishStatus });
  }

  deleteVideoSeries(id: string, orgId: string): Observable<{ success: boolean; message: string }> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.delete<{ success: boolean; message: string }>(
      `${this._apiBase}/${id}`,
      { params: httpParams }
    );
  }

  // ─── Episodes ─────────────────────────────────────────────────────────────

  addEpisode(seriesId: string, dto: AddVideoEpisodeDto): Observable<ApiResponse<VideoSeries>> {
    return this._http.post<ApiResponse<VideoSeries>>(`${this._apiBase}/${seriesId}/episodes`, dto);
  }

  removeEpisode(seriesId: string, episodeId: string, orgId: string): Observable<ApiResponse<VideoSeries>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.delete<ApiResponse<VideoSeries>>(
      `${this._apiBase}/${seriesId}/episodes/${episodeId}`,
      { params: httpParams }
    );
  }

  reorderEpisodes(seriesId: string, dto: ReorderVideoEpisodesDto): Observable<ApiResponse<VideoSeries>> {
    return this._http.patch<ApiResponse<VideoSeries>>(
      `${this._apiBase}/${seriesId}/episodes/reorder`,
      dto
    );
  }
  // ─── Reader (authenticated, visibility-filtered) ───────────────────────────

  getReaderVideoSeries(orgId: string, page?: number, limit?: number): Observable<PaginatedResponse<VideoSeries>> {
    let params = new HttpParams().set('orgId', orgId);
    if (page !== undefined) params = params.set('page', String(page));
    if (limit !== undefined) params = params.set('limit', String(limit));
    return this._http.get<PaginatedResponse<VideoSeries>>(this._readerBase, { params });
  }

  getReaderVideoSeriesBySlug(slug: string, orgId: string): Observable<ApiResponse<VideoSeries>> {
    const params = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<VideoSeries>>(`${this._readerBase}/${slug}`, { params });
  }
  // ─── Helpers ──────────────────────────────────────────────────────────────

  private _toStringParams(obj: Record<string, unknown>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)])
    );
  }
}
